import React, { useState } from 'react';
import type { FC } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box } from '@clutter/clean';

import { FormGroup, Table } from '@shared/components/bootstrap';
import { NavLinkButton } from '@shared/components/bootstrap/button';
import { Panel } from '@shared/components/bootstrap/panel';
import { UserRole } from '@shared/types/user_role';

import { Permission } from '@admin/components/helpers/permission';
import { Roles } from '@admin/components/helpers/roles';
import { Selector as WarehouseMultiselect } from '@admin/components/warehouses/selector';
import { editVehicleURL, newVehicleURL, vehicleTypesURL, vehicleURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { VEHICLE, VEHICLE_STATUS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useVehiclesListQuery, Vehicle__Status } from '@admin/schema';

import { Pagination } from '../pagination';
import { VehicleTypeSelectFormGroup } from '../vehicle_types/select_form_group';
import { VehicleStatusLabel } from './status_label';
import { VehicleStatusSelectFormGroup } from './status_select_form_group';

const DEFAULT_PAGE = 1;

export const VehicleList: FC = () => {
  useTitle('Fleet Management');
  const canReadVehicles = usePermission(VEHICLE.resource, VEHICLE.actions.read);
  const canCreateVehicles = usePermission(VEHICLE.resource, VEHICLE.actions.create);
  const canUpdateVehicles = usePermission(VEHICLE.resource, VEHICLE.actions.update);
  const canUpdateVehicleStatus = usePermission(VEHICLE_STATUS.resource, VEHICLE_STATUS.actions.update);
  const canEditVehicles = canUpdateVehicles || canUpdateVehicleStatus;
  const [typeID, setTypeID] = useState<string | undefined>();
  const [warehouseIDs, setWarehouseIDs] = useState<string[]>([]);
  const [status, setStatus] = useState<Vehicle__Status | undefined>();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { data } = useVehiclesListQuery({
    client,
    skip: !canReadVehicles,
    variables: {
      page: page,
      warehouseIDs: warehouseIDs.length > 0 ? warehouseIDs : undefined,
      status: status,
    },
  });

  const paginated = data?.paginated;
  const vehicles = paginated?.results;

  return (
    <Permission resource={VEHICLE.resource} action={VEHICLE.actions.read}>
      <Panel>
        <Panel.Body>
          <div className="row">
            <div className="col-md-4">
              <FormGroup>
                <label>Warehouse:</label>
                <WarehouseMultiselect selectedIDs={warehouseIDs} onChange={setWarehouseIDs} />
              </FormGroup>
            </div>
            <div className="col-md-4">
              <VehicleStatusSelectFormGroup id="status" label="Status:" value={status} onChange={setStatus} />
            </div>
            <div className="col-md-4">
              <VehicleTypeSelectFormGroup id="type_id" label="Type:" value={typeID} onChange={setTypeID} />
            </div>
          </div>
        </Panel.Body>
        <Panel.Footer align="right">
          <Roles show={[UserRole.Admin]}>
            <NavLinkButton to={vehicleTypesURL()} kind="primary">
              View Vehicle Types
            </NavLinkButton>
          </Roles>
        </Panel.Footer>
      </Panel>

      <Panel>
        <Panel.Body>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-2">Serial Code</th>
                <th className="col-sm-2">Warehouse</th>
                <th className="col-sm-2">Region</th>
                <th className="col-sm-1">Status</th>
                <th className="col-sm-1">Type</th>
                <th className="col-sm-2">License Plate</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {vehicles?.map((vehicle) => (
                <tr key={vehicle.id}>
                  <td className="col-sm-2">{vehicle.serialCode}</td>
                  <td className="col-sm-2">{vehicle.warehouse.name}</td>
                  <td className="col-sm-2">{vehicle.region.name}</td>
                  <td className="col-sm-1">
                    <VehicleStatusLabel vehicle={vehicle} />
                  </td>
                  <td className="col-sm-1">{vehicle.typeName}</td>
                  <td className="col-sm-2">{vehicle.licensePlate ?? '-'}</td>
                  <td className="col-zero col-nowrap">
                    <Box.Flex gap="4px">
                      <NavLinkButton kind="primary" to={vehicleURL(vehicle)}>
                        View
                      </NavLinkButton>
                      {canEditVehicles && (
                        <NavLinkButton kind="primary" to={editVehicleURL(vehicle)}>
                          Edit
                        </NavLinkButton>
                      )}
                    </Box.Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {paginated?.pagination && <Pagination pagination={paginated.pagination} onPage={setPage} />}
        </Panel.Body>
        {canCreateVehicles && (
          <Panel.Footer align="right">
            <NavLinkButton to={newVehicleURL()} kind="mint">
              <FontAwesomeIcon icon={faPlus} /> Add
            </NavLinkButton>
          </Panel.Footer>
        )}
      </Panel>
    </Permission>
  );
};
